/**
 * @file pages/Endpoints.jsx
 * @description Endpoint logging configuration page.
 *
 * Features:
 *  - Lists all endpoint configs with path, method, and logging status
 *  - Inline toggle switch to enable/disable logging per endpoint
 *  - Add new config form (inline, not a modal)
 *  - Delete config (admin only)
 *
 * A wildcard row (path='*', method='*') is highlighted as the global toggle.
 * Access: admin and operator roles only.
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import DataTable from '../components/DataTable';
import {
  listEndpointConfigs,
  createEndpointConfig,
  updateEndpointConfig,
  deleteEndpointConfig,
} from '../api/endpointConfigs';

/** HTTP method options for the add-config form. */
const METHOD_OPTIONS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', '*'];

/** Initial state for the add-config form. */
const EMPTY_FORM = { path: '', method: 'GET', logging_enabled: true };

/**
 * Endpoint configuration page component.
 */
export default function Endpoints() {
  const queryClient = useQueryClient();

  /** Controls visibility of the inline add form. */
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]         = useState(EMPTY_FORM);

  const { data: configs = [], isLoading, error } = useQuery({
    queryKey: ['endpointConfigs'],
    queryFn:  listEndpointConfigs,
  });

  // Toggle mutation — fires when the user flips the logging switch
  const toggleMutation = useMutation({
    mutationFn: ({ id, logging_enabled }) => updateEndpointConfig(id, { logging_enabled }),
    onSuccess:  () => queryClient.invalidateQueries({ queryKey: ['endpointConfigs'] }),
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: createEndpointConfig,
    onSuccess:  () => {
      queryClient.invalidateQueries({ queryKey: ['endpointConfigs'] });
      setForm(EMPTY_FORM);
      setShowForm(false);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: deleteEndpointConfig,
    onSuccess:  () => queryClient.invalidateQueries({ queryKey: ['endpointConfigs'] }),
  });

  const handleToggle = (config) => {
    toggleMutation.mutate({ id: config.id, logging_enabled: !config.logging_enabled });
  };

  const handleDelete = (config) => {
    if (window.confirm(`Delete config for ${config.method} ${config.path}?`)) {
      deleteMutation.mutate(config.id);
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    createMutation.mutate(form);
  };

  /** DataTable column definitions */
  const columns = [
    {
      key: 'path',
      label: 'Path',
      render: (row) => (
        <span>
          {row.path === '*' && row.method === '*' && (
            <span
              className="badge me-2"
              style={{ backgroundColor: 'var(--color-accent-1)', color: '#000' }}
            >
              Global
            </span>
          )}
          <code>{row.path}</code>
        </span>
      ),
    },
    {
      key: 'method',
      label: 'Method',
      render: (row) => (
        <span className="badge"
          style={{ backgroundColor: 'var(--color-primary)', color: 'var(--color-text-light)' }}>
          {row.method}
        </span>
      ),
    },
    {
      key: 'logging_enabled',
      label: 'Logging',
      render: (row) => (
        // Accessible toggle switch — updates on change without a save button
        <div className="form-check form-switch mb-0" title={row.logging_enabled ? 'Disable logging' : 'Enable logging'}>
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            id={`toggle-${row.id}`}
            checked={row.logging_enabled}
            onChange={() => handleToggle(row)}
            disabled={toggleMutation.isPending}
            aria-label={`Toggle logging for ${row.method} ${row.path}`}
            style={{ cursor: 'pointer' }}
          />
        </div>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (row) => (
        <button
          className="btn btn-sm btn-outline-danger"
          onClick={() => handleDelete(row)}
          disabled={deleteMutation.isPending}
          aria-label={`Delete config for ${row.method} ${row.path}`}
        >
          <i className="bi bi-trash me-1" aria-hidden="true" />
          Delete
        </button>
      ),
    },
  ];

  return (
    <div className="page-content">
      {/* Page header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="h4 fw-bold mb-0" style={{ color: 'var(--color-primary)' }}>
          <i className="bi bi-toggles me-2" aria-hidden="true" />
          Endpoint Logging
        </h1>
        <button
          className="btn"
          style={{ backgroundColor: 'var(--color-primary)', color: 'var(--color-text-light)' }}
          onClick={() => setShowForm((v) => !v)}
        >
          <i className={`bi ${showForm ? 'bi-dash-circle' : 'bi-plus-circle'} me-1`} aria-hidden="true" />
          {showForm ? 'Cancel' : 'Add Config'}
        </button>
      </div>

      {/* Inline add config form */}
      {showForm && (
        <div className="card mb-4 border-0 shadow-sm">
          <div
            className="card-header fw-semibold"
            style={{ backgroundColor: 'var(--color-primary)', color: 'var(--color-text-light)' }}
          >
            New Endpoint Config
          </div>
          <div className="card-body">
            <form onSubmit={handleFormSubmit} className="row g-3 align-items-end">
              <div className="col-md-5">
                <label htmlFor="cfg-path" className="form-label fw-semibold">Path</label>
                <input
                  id="cfg-path"
                  type="text"
                  className="form-control"
                  value={form.path}
                  onChange={(e) => setForm((p) => ({ ...p, path: e.target.value }))}
                  placeholder="/api/tokens or *"
                  required
                />
              </div>
              <div className="col-md-3">
                <label htmlFor="cfg-method" className="form-label fw-semibold">Method</label>
                <select
                  id="cfg-method"
                  className="form-select"
                  value={form.method}
                  onChange={(e) => setForm((p) => ({ ...p, method: e.target.value }))}
                >
                  {METHOD_OPTIONS.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-2 d-flex align-items-center gap-2 pt-3">
                <input
                  id="cfg-logging"
                  type="checkbox"
                  className="form-check-input"
                  role="switch"
                  checked={form.logging_enabled}
                  onChange={(e) => setForm((p) => ({ ...p, logging_enabled: e.target.checked }))}
                />
                <label htmlFor="cfg-logging" className="form-check-label fw-semibold">
                  Logging on
                </label>
              </div>
              <div className="col-md-2">
                <button
                  type="submit"
                  className="btn w-100"
                  style={{ backgroundColor: 'var(--color-accent-2)', color: '#000' }}
                  disabled={createMutation.isPending}
                >
                  {createMutation.isPending ? 'Adding…' : 'Add'}
                </button>
              </div>
              {createMutation.isError && (
                <div className="col-12">
                  <div className="alert alert-danger mb-0" role="alert">
                    {createMutation.error?.message || 'Failed to create config.'}
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      )}

      {/* Config list */}
      <div className="card">
        <div className="card-body p-0">
          <DataTable
            columns={columns}
            data={configs}
            isLoading={isLoading}
            error={error?.message}
            emptyMessage="No endpoint configs. Add one to start logging."
            getRowKey={(row) => row.id}
          />
        </div>
      </div>
    </div>
  );
}
